import React, { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../lib/auth-store';
import { Shield, Search, Filter, ChevronLeft, ChevronRight, Activity, Users, AlertTriangle, Eye, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  auth: 'bg-blue-100 text-blue-800',
  leads: 'bg-emerald-100 text-emerald-800',
  applications: 'bg-violet-100 text-violet-800',
  pipeline: 'bg-amber-100 text-amber-800',
  equipment: 'bg-cyan-100 text-cyan-800',
  documents: 'bg-pink-100 text-pink-800',
  admin: 'bg-red-100 text-red-800',
  settings: 'bg-gray-100 text-gray-800',
  processor: 'bg-indigo-100 text-indigo-800',
  residuals: 'bg-lime-100 text-lime-800',
  orders: 'bg-orange-100 text-orange-800',
  data_access: 'bg-rose-100 text-rose-800',
  tasks: 'bg-sky-100 text-sky-800',
  prospecting: 'bg-teal-100 text-teal-800',
  email: 'bg-fuchsia-100 text-fuchsia-800',
  general: 'bg-slate-100 text-slate-800',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'auth', label: 'Authentication' },
  { value: 'leads', label: 'Leads' },
  { value: 'applications', label: 'Applications' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'email', label: 'Email' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'documents', label: 'Documents' },
  { value: 'admin', label: 'Admin' },
  { value: 'settings', label: 'Settings' },
  { value: 'processor', label: 'Processor' },
  { value: 'data_access', label: 'Data Access' },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );
}

export default function ActivityLogsPage() {
  const { token } = useAuthStore();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [unackCount, setUnackCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [logType, setLogType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [logUsers, setLogUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' | 'alerts'

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (category) params.set('category', category);
      if (logType) params.set('log_type', logType);
      if (search) params.set('search', search);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (userFilter) params.set('user_id', userFilter);

      const res = await fetch(`${API}/api/logs?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, page, category, logType, search, dateFrom, dateTo, userFilter]);

  const fetchLogUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/logs/users`, { headers });
      if (res.ok) setLogUsers(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/logs/stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/security/alerts?limit=50`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
        setUnackCount(data.unacknowledged_count || 0);
      }
    } catch (e) { console.error(e); }
  }, [token]);

  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API}/api/security/alerts/${alertId}/acknowledge`, {
        method: 'POST', headers,
      });
      if (res.ok) fetchAlerts();
    } catch (e) { console.error(e); }
  };

  const runScan = async () => {
    try {
      const res = await fetch(`${API}/api/security/scan`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        fetchAlerts();
        if (data.new_alerts > 0) {
          alert(`Scan complete: ${data.new_alerts} new alert(s) detected`);
        }
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => { fetchLogUsers(); }, [fetchLogUsers]);

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div data-testid="activity-logs-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Activity Logs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every action in the CRM is tracked with full user attribution
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="run-scan-btn"
            onClick={runScan}
            className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm font-medium transition-colors"
          >
            Run Security Scan
          </button>
          <button
            data-testid="refresh-logs-btn"
            onClick={() => { fetchLogs(); fetchStats(); fetchAlerts(); }}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div data-testid="log-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Events (24h)" value={stats.total_events_24h || 0} color="bg-blue-50 text-blue-600" />
          <StatCard icon={Users} label="Active Users" value={stats.active_users_24h || 0} color="bg-emerald-50 text-emerald-600" />
          <StatCard icon={Eye} label="Data Access" value={stats.sensitive_data_access_24h || 0} color="bg-amber-50 text-amber-600" />
          <StatCard icon={AlertTriangle} label="Failed Auth" value={stats.failed_auth_24h || 0} color="bg-red-50 text-red-600" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
        <button
          data-testid="tab-logs"
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Activity Logs
        </button>
        <button
          data-testid="tab-alerts"
          onClick={() => setActiveTab('alerts')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${activeTab === 'alerts' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          Security Alerts
          {unackCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{unackCount}</span>
          )}
        </button>
      </div>

      {/* Security Alerts Panel */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-400">
              No security alerts. System looks clean.
            </div>
          ) : alerts.map((alert, i) => (
            <div
              key={i}
              data-testid={`alert-${i}`}
              className={`bg-white border rounded-lg p-4 flex items-start justify-between gap-4 ${
                alert.severity === 'critical' ? 'border-red-300 bg-red-50' :
                alert.severity === 'high' ? 'border-orange-300 bg-orange-50' :
                alert.severity === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                'border-slate-200'
              } ${alert.acknowledged ? 'opacity-60' : ''}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                    alert.severity === 'critical' ? 'bg-red-200 text-red-800' :
                    alert.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                    alert.severity === 'warning' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-slate-200 text-slate-700'
                  }`}>{alert.severity}</span>
                  <span className="text-xs text-slate-500">{alert.alert_type?.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-slate-400">{formatTime(alert.created_at)}</span>
                </div>
                <div className="font-semibold text-slate-800">{alert.title}</div>
                <div className="text-sm text-slate-600 mt-1">{alert.description}</div>
                {alert.ip_address && <div className="text-xs text-slate-400 mt-1 font-mono">IP: {alert.ip_address}</div>}
              </div>
              {!alert.acknowledged && (
                <button
                  onClick={() => acknowledgeAlert(alert.id || alert._id)}
                  className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 rounded-lg whitespace-nowrap"
                >
                  Acknowledge
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Logs Tab Content */}
      {activeTab === 'logs' && (<>
      {/* Filters */}
      <div data-testid="log-filters" className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="log-search-input"
                type="text"
                placeholder="Search actions, users, paths..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Rep</label>
            <select
              data-testid="log-user-filter"
              value={userFilter}
              onChange={e => { setUserFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[160px]"
            >
              <option value="">All Reps</option>
              {logUsers.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.user_email || u.user_id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
            <select
              data-testid="log-category-filter"
              value={category}
              onChange={e => { setCategory(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
            <select
              data-testid="log-type-filter"
              value={logType}
              onChange={e => { setLogType(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
            >
              <option value="">All Types</option>
              <option value="request">HTTP Requests</option>
              <option value="action">Actions</option>
              <option value="data_access">Data Access</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
            <input
              data-testid="log-date-from"
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
            <input
              data-testid="log-date-to"
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="logs-table" className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Time</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">User</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Action</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Category</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">IP</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-600">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400">No activity logs found</td></tr>
              ) : logs.map((log, i) => (
                <tr key={i} data-testid={`log-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                    <Clock className="inline w-3 h-3 mr-1 opacity-50" />
                    {formatTime(log.timestamp)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-medium text-slate-800">{log.user_email || log.user_id || 'System'}</span>
                    {log.actor === 'system' && (
                      <span data-testid={`log-auto-badge-${i}`} className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700">
                        Auto
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      {log.user_role || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700">{log.action}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[log.category] || CATEGORY_COLORS.general}`}>
                      {log.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500 font-mono">{log.ip_address || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.status_code ? (
                      <span className={`px-2 py-0.5 rounded text-xs font-mono ${log.status_code < 400 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {log.status_code}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-[250px] truncate">
                    {log.description || log.path || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div data-testid="log-pagination" className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <span className="text-sm text-slate-500">{total} total events</span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Top Users (if stats available) */}
      </>)}
      {activeTab === 'logs' && stats?.top_users?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Most Active Users (24h)</h3>
          <div className="space-y-2">
            {stats.top_users.map((u, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-700">{u.user_email || u.user_id}</span>
                  <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-500">{u.user_role}</span>
                </div>
                <span className="font-mono text-slate-500">{u.count} actions</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
